import { useState, type ReactNode } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import RevenueInputScreen from '../components/Onboarding/RevenueInputScreen';
import ReceiptsScreen from '../components/Onboarding/ReceiptsScreen';
import { FIRST_RUN_STATE } from '../components/Onboarding/firstRun';
import { useFirstRun } from '../contexts/FirstRunContext';
import { useOnboarding } from '../contexts/OnboardingContext';

const DEFAULT_REVENUE = 2000;

/** Step 1 of first run. Its halo glows past the edges, so clip sideways only. */
function RevenuePage({ children }: { children: ReactNode }) {
  return (
    <div style={{ maxWidth: 720, margin: '0 auto', position: 'relative', overflowX: 'clip' }}>
      {children}
    </div>
  );
}

/** /welcome: "Roughly how much do you earn?" */
export function WelcomeRevenue() {
  const navigate = useNavigate();
  const { revenue, setRevenue } = useFirstRun();
  // The slider is a draft; the number only counts once they move on, so a
  // skip from here leaves no revenue behind.
  const [draft, setDraft] = useState(revenue ?? DEFAULT_REVENUE);

  return (
    <RevenuePage>
      <RevenueInputScreen
        revenue={draft}
        onRevenueChange={setDraft}
        onNext={(value) => {
          setRevenue(value);
          navigate('/welcome/result', { state: FIRST_RUN_STATE });
        }}
      />
    </RevenuePage>
  );
}

/** /welcome/result: what a winning test is worth at that revenue. */
export function WelcomeResult() {
  const navigate = useNavigate();
  const { revenue } = useFirstRun();
  const { isOnboardingComplete, completeOnboarding } = useOnboarding();

  // Revenue is session-only, so a reload lands here without it.
  if (revenue === null) {
    return <Navigate to="/welcome" replace state={FIRST_RUN_STATE} />;
  }

  return (
    <RevenuePage>
      <ReceiptsScreen
        revenue={revenue}
        onBack={() => navigate('/welcome', { state: FIRST_RUN_STATE })}
        onNext={() => {
          // Done with the intro: from here / is Home, and the rest of first
          // run lives on the shared chooser and wizard pages.
          if (!isOnboardingComplete) void completeOnboarding();
          navigate('/new', { state: FIRST_RUN_STATE });
        }}
      />
    </RevenuePage>
  );
}
